"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Field,
  TextInput,
  SelectInput,
  CheckboxRow,
  ResultBanner,
  PrintButton,
  CompletionPanel,
  SegmentedControl,
  ActionPlanner,
  ActionPlanPrint,
  useSavedActions,
  PrintFooter,
  trackToolResult,
  type Verdict,
} from "@/components/tools/ui";

/* Grounded in the SSIP Core Criteria (the HSE-approved threshold standard that
   CHAS, SafeContractor, SMAS and Constructionline all assess against — mutual
   recognition / "deem to satisfy"). Items mirror what an assessor samples:
   signed H&S policy & organisation, site-specific RAMS, training/competence,
   accident records & RIDDOR, insurance, subcontractor monitoring, and
   welfare/COSHH/PPE arrangements.

   Sources: ssip.org.uk/ssip-core-criteria; Once-For-All Health & Safety SSIP
   Question Set; hse.gov.uk competence/accreditation-schemes. Kept generic-SSIP
   — no scheme-specific demands invented.

   Compliance: this is a self-assessment of evidence readiness only. It never
   predicts or guarantees an accreditation outcome. */

type Size = "sole" | "small" | "large";

interface Item {
  id: string;
  label: string;
  hint?: string;
  /** Show only at/above this employee threshold. */
  minSize?: Size;
  /** Only relevant when the contractor uses subcontractors. */
  needsSubbies?: boolean;
  /** Weight in the group score — core legal/threshold items count double. */
  weight?: number;
}

interface Group {
  id: string;
  title: string;
  blurb: string;
  items: Item[];
}

const SIZE_RANK: Record<Size, number> = { sole: 0, small: 1, large: 2 };

const GROUPS: Group[] = [
  {
    id: "policy",
    title: "H&S policy & organisation",
    blurb:
      "Assessors check for a written policy with a statement of intent signed by the most senior person and dated within the last 12 months, plus who is responsible for health and safety.",
    items: [
      {
        id: "policy-written",
        label: "Written health & safety policy (statement of intent)",
        hint: "A documented policy is a legal duty once you have 5 or more employees, and assessors expect one regardless of size.",
        weight: 2,
      },
      {
        id: "policy-signed",
        label: "Policy signed by the MD / owner and dated within the last 12 months",
        hint: "The statement of intent must carry a current signature from the most senior person.",
        weight: 2,
      },
      {
        id: "policy-org",
        label: "Defined roles, responsibilities and chain of command for H&S",
        hint: "Who is responsible for what — from senior management down to individual workers.",
      },
      {
        id: "policy-named",
        label: "A named, competent person responsible for health & safety",
        hint: "Held internally or via an external adviser — the named contact is typically a company director.",
        weight: 2,
      },
      {
        id: "policy-arrangements",
        label: "Written arrangements / procedures for how you manage H&S day to day",
        hint: "How you put the policy into practice: risk assessment, consultation, training, monitoring, accident reporting.",
      },
    ],
  },
  {
    id: "rams",
    title: "Risk assessments & method statements",
    blurb:
      "The most common reason for a referral. Assessors typically ask for two site-specific example RAMS from separate projects — generic, off-the-shelf documents are spotted immediately.",
    items: [
      {
        id: "rams-process",
        label: "A process for producing suitable & sufficient risk assessments",
        hint: "Identify hazards, assess risk, record the controls put in place.",
        weight: 2,
      },
      {
        id: "rams-examples",
        label: "Two site-specific example RAMS from separate, recent projects",
        hint: "Tied to your actual work activities — not a template with the company name swapped in.",
        weight: 2,
      },
      {
        id: "rams-methods",
        label: "Method statements (safe systems of work) for your higher-risk tasks",
        hint: "The step-by-step sequence for the task, matched to the risk assessment.",
      },
      {
        id: "rams-cdm",
        label: "Construction Phase Plan example (where you do construction work)",
        hint: "CDM 2015 requires a CPP before construction work starts — even on small/domestic jobs.",
      },
    ],
  },
  {
    id: "training",
    title: "Training & competence records",
    blurb:
      "Evidence that your workforce has the training and information appropriate to the work — usually a training matrix with expiry dates plus the underlying certificates.",
    items: [
      {
        id: "train-matrix",
        label: "Up-to-date training matrix with expiry / renewal dates",
        hint: "A single view of who is trained in what and when it lapses.",
        weight: 2,
      },
      {
        id: "train-certs",
        label: "Training certificates for legally required topics",
        hint: "For example asbestos awareness, working at height, manual handling — appropriate to your trade.",
      },
      {
        id: "train-cards",
        label: "Competence / skills cards for site work (e.g. CSCS-type cards)",
        hint: "Many sites require a valid card before workers can start.",
      },
      {
        id: "train-firstaid",
        label: "First aid provision appropriate to your workforce and sites",
        hint: "Trained first aider(s) or appointed person, sized to the work.",
      },
    ],
  },
  {
    id: "incidents",
    title: "Accident / incident records & RIDDOR",
    blurb:
      "Assessors look for evidence that you record incidents and understand your duty to report the serious ones to the HSE under RIDDOR.",
    items: [
      {
        id: "inc-records",
        label: "An accident book / incident records you keep up to date",
        weight: 2,
      },
      {
        id: "inc-procedure",
        label: "A written procedure for reporting and investigating incidents",
      },
      {
        id: "inc-riddor",
        label: "Awareness of RIDDOR — what is reportable and the deadlines",
        hint: "Specified injuries, over-7-day absences, dangerous occurrences and certain diseases.",
      },
    ],
  },
  {
    id: "insurance",
    title: "Insurance",
    blurb:
      "Current certificates are a standard part of the assessment. Employers' liability is a legal requirement once you employ staff.",
    items: [
      {
        id: "ins-el",
        label: "Employers' liability insurance certificate (current)",
        hint: "A legal requirement once you have employees — minimum £5m cover.",
        minSize: "small",
        weight: 2,
      },
      {
        id: "ins-pl",
        label: "Public liability insurance certificate (current)",
        hint: "Almost always required by clients and principal contractors.",
        weight: 2,
      },
      {
        id: "ins-pi",
        label: "Professional indemnity cover where you carry design responsibility",
        hint: "Relevant if you take on any design element under CDM.",
      },
    ],
  },
  {
    id: "subcontractors",
    title: "Subcontractor management",
    blurb:
      "If you use subcontractors, assessors expect evidence that you check their competence and monitor their performance on site.",
    items: [
      {
        id: "sub-competence",
        label: "A process for checking subcontractor competence before appointment",
        hint: "Their SSIP status, H&S policy, training and insurances.",
        needsSubbies: true,
        weight: 2,
      },
      {
        id: "sub-monitor",
        label: "Evidence you monitor subcontractor performance on site",
        hint: "A recent site inspection report naming the subcontractor, or a post-works review.",
        needsSubbies: true,
      },
    ],
  },
  {
    id: "arrangements",
    title: "Welfare, COSHH & PPE arrangements",
    blurb:
      "Where relevant to your work, assessors check the practical arrangements: welfare facilities, control of hazardous substances and provision of PPE.",
    items: [
      {
        id: "arr-welfare",
        label: "Welfare arrangements for your sites (toilets, washing, rest, drinking water)",
      },
      {
        id: "arr-coshh",
        label: "COSHH assessments for hazardous substances you use",
        hint: "One per substance — silica/dust, solvents, adhesives etc — linked to your method statements.",
      },
      {
        id: "arr-ppe",
        label: "A process for selecting, issuing and recording PPE",
        hint: "The right PPE specified for the task, issued and replaced as needed.",
      },
    ],
  },
];

type State = Record<string, "yes" | "partial" | "no">;

const ANSWER_VALUE: Record<"yes" | "partial" | "no", number> = { yes: 1, partial: 0.5, no: 0 };

function itemApplies(item: Item, size: Size, usesSubbies: boolean): boolean {
  if (item.minSize && SIZE_RANK[size] < SIZE_RANK[item.minSize]) return false;
  if (item.needsSubbies && !usesSubbies) return false;
  return true;
}

export function SsipReadinessChecker() {
  const [size, setSize] = useState<Size | "">("");
  const [usesSubbies, setUsesSubbies] = useState(false);
  const [company, setCompany] = useState("");
  const [answers, setAnswers] = useState<State>({});
  // Owner/due per open gap — turns the gap list into an action plan.
  const [actions, setAction, clearActions] = useSavedActions("ssip-readiness-checker");
  const resultTracked = useRef(false);

  const setAnswer = (id: string, v: "yes" | "partial" | "no") =>
    setAnswers((a) => ({ ...a, [id]: v }));

  const computed = useMemo(() => {
    if (!size) return null;
    const groups = GROUPS.map((g) => {
      const items = g.items.filter((it) => itemApplies(it, size, usesSubbies));
      if (items.length === 0) return null;
      const maxWeight = items.reduce((s, it) => s + (it.weight ?? 1), 0);
      const gotWeight = items.reduce((s, it) => {
        const ans = answers[it.id] ?? "no";
        return s + (it.weight ?? 1) * ANSWER_VALUE[ans];
      }, 0);
      const pct = maxWeight > 0 ? Math.round((gotWeight / maxWeight) * 100) : 0;
      // Gaps: anything not a clear "yes", weighted items first.
      const gaps = items
        .filter((it) => (answers[it.id] ?? "no") !== "yes")
        .sort((a, b) => (b.weight ?? 1) - (a.weight ?? 1));
      return { group: g, items, pct, gaps };
    }).filter((x): x is NonNullable<typeof x> => x !== null);

    const overallMax = groups.reduce(
      (s, g) => s + g.items.reduce((t, it) => t + (it.weight ?? 1), 0),
      0,
    );
    const overallGot = groups.reduce(
      (s, g) =>
        s +
        g.items.reduce(
          (t, it) => t + (it.weight ?? 1) * ANSWER_VALUE[answers[it.id] ?? "no"],
          0,
        ),
      0,
    );
    const overall = overallMax > 0 ? Math.round((overallGot / overallMax) * 100) : 0;

    // Banner is about evidence readiness, never a pass/fail prediction.
    let level: Verdict;
    let title: string;
    let advice: string;
    if (overall >= 85) {
      level = "low";
      title = `${overall}% ready — you have most of the evidence an assessor expects to see`;
      advice =
        "You've ticked off the bulk of the SSIP Core Criteria. Pull the documents together in one place, make sure every dated item (policy signature, certificates, training) is current within the last 12 months, and review the few gaps below before you submit. This is a readiness check, not a guarantee of the outcome.";
    } else if (overall >= 55) {
      level = "medium";
      title = `${overall}% ready — some gaps to close before you submit`;
      advice =
        "You have a solid base but there are gaps an assessor would likely query. Work through the prioritised list below, starting with the core items (signed policy, site-specific RAMS, insurances and competence records).";
    } else {
      level = "high";
      title = `${overall}% ready — significant gaps to address first`;
      advice =
        "There's meaningful groundwork to do before submitting. Focus on the foundations first — a signed, current H&S policy, two site-specific example RAMS, current insurance certificates and training records — then build out the rest.";
    }

    const allGaps = groups.flatMap((g) =>
      g.gaps.map((it) => ({ group: g.group.title, item: it })),
    );

    return { groups, overall, level, title, advice, allGaps };
  }, [size, usesSubbies, answers]);

  useEffect(() => {
    if (computed && !resultTracked.current) {
      // Only fire once at least one group has been answered meaningfully.
      const answered = Object.keys(answers).length > 0;
      if (answered) {
        resultTracked.current = true;
        trackToolResult("ssip-readiness-checker", { score: computed.overall });
      }
    }
  }, [computed, answers]);

  function bandClass(pct: number): string {
    if (pct >= 85) return "text-emerald-deep";
    if (pct >= 55) return "text-[#8a6400]";
    return "text-[#b34e10]";
  }

  const gapItems = (computed?.allGaps ?? []).map(({ group, item }) => ({
    id: item.id,
    label: `${item.label} (${group})`,
  }));

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
      {/* Inputs */}
      <div className="space-y-6">
        <div className="card shadow-soft p-6">
          <h2 className="text-xl">About your business</h2>
          <p className="mt-1 text-sm text-muted">
            A couple of details so the checklist only asks for what actually applies to you — some
            requirements (like a written H&amp;S policy being a legal duty) only bite at five or more
            employees.
          </p>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <Field label="Company size" hint="Number of people you employ.">
              <SelectInput value={size} onChange={(e) => setSize(e.target.value as Size | "")}>
                <option value="">Select…</option>
                <option value="sole">Sole trader (just me)</option>
                <option value="small">2–4 employees</option>
                <option value="large">5 or more employees</option>
              </SelectInput>
            </Field>
            <Field label="Company name" hint="For the printed gap report (optional).">
              <TextInput
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder="e.g. Acme Heating Ltd"
              />
            </Field>
          </div>
          <div className="mt-4">
            <CheckboxRow checked={usesSubbies} onChange={setUsesSubbies}>
              We use subcontractors on our work
            </CheckboxRow>
          </div>
        </div>

        {size ? (
          computed!.groups.map(({ group, items, pct }) => (
            <div key={group.id} className="card shadow-soft p-6">
              <div className="flex items-start justify-between gap-3">
                <h2 className="text-lg">{group.title}</h2>
                <span className={`shrink-0 font-display text-lg font-bold ${bandClass(pct)}`}>
                  {pct}%
                </span>
              </div>
              <p className="mt-1 text-sm text-muted">{group.blurb}</p>
              <div className="mt-4 space-y-3">
                {items.map((it) => {
                  // Undefined until answered — unanswered scores as "no" but is
                  // not shown pre-selected, so the progress count stays honest.
                  const current = answers[it.id];
                  return (
                    <div key={it.id} className="rounded-lg border border-line bg-mist/50 p-3.5">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-[200px] flex-1">
                          <div className="text-[0.92rem] font-semibold leading-snug text-ink">
                            {it.label}
                          </div>
                          {it.hint && (
                            <div className="mt-1 text-[0.8rem] leading-snug text-muted">{it.hint}</div>
                          )}
                        </div>
                        <SegmentedControl
                          legend={it.label}
                          name={`ssip-${it.id}`}
                          value={current}
                          onChange={(v) => setAnswer(it.id, v)}
                          className="shrink-0"
                          options={[
                            { value: "yes", label: "Have it", activeCls: "border-emerald bg-emerald-tint text-emerald-deep" },
                            { value: "partial", label: "In progress", activeCls: "border-risk-medium bg-[#fdf6e3] text-[#8a6400]" },
                            { value: "no", label: "Missing", activeCls: "border-risk-high bg-[#fef0e6] text-[#b34e10]" },
                          ]}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        ) : (
          <div className="card border-dashed p-6 text-center text-sm text-muted">
            Choose your company size to load the checklist tailored to your obligations.
          </div>
        )}
      </div>

      {/* Result + printable gap report */}
      <div className="space-y-4">
        {computed && (
          <div className="lg:sticky lg:top-20 lg:z-10">
            <CompletionPanel
              title="Checklist progress"
              items={computed.groups.flatMap(({ group, items }) =>
                items.map((it) => ({
                  label: `${it.label} (${group.title})`,
                  done: answers[it.id] !== undefined,
                })),
              )}
            />
          </div>
        )}
        {computed && Object.keys(answers).length > 0 ? (
          <>
            <div className="print-area card shadow-soft p-6" id="ssip-report">
              <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">
                SSIP readiness — gap report
              </div>
              <h2 className="mt-1 text-lg">
                {company ? `${company} — ` : ""}
                {new Date().toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </h2>

              <div className="mt-4">
                <ResultBanner level={computed.level} title={computed.title}>
                  {computed.advice}
                </ResultBanner>
              </div>

              {/* Per-group readiness */}
              <div className="mt-5">
                <div className="text-[0.78rem] font-semibold uppercase tracking-wide text-muted">
                  Readiness by area
                </div>
                <div className="mt-2 space-y-2">
                  {computed.groups.map(({ group, pct }) => (
                    <div key={group.id} className="flex items-center gap-3">
                      <div className="w-44 shrink-0 text-[0.85rem] text-ink">{group.title}</div>
                      <div className="h-2 flex-1 overflow-hidden rounded-full bg-mist">
                        <div
                          className={`h-full rounded-full ${
                            pct >= 85 ? "bg-risk-low" : pct >= 55 ? "bg-risk-medium" : "bg-risk-high"
                          }`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <div className={`w-10 shrink-0 text-right text-[0.85rem] font-bold ${bandClass(pct)}`}>
                        {pct}%
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Prioritised gaps */}
              <div className="mt-5">
                <div className="text-[0.78rem] font-semibold uppercase tracking-wide text-muted">
                  Gaps to close{computed.allGaps.length > 0 ? ` (${computed.allGaps.length})` : ""}
                </div>
                {computed.allGaps.length === 0 ? (
                  <p className="mt-2 text-[0.9rem] text-ink">
                    Nothing outstanding on this checklist. Make sure every dated document is current
                    within the last 12 months before you submit.
                  </p>
                ) : (
                  <ol className="mt-2 space-y-2">
                    {computed.allGaps.map(({ group, item }) => (
                      <li key={item.id} className="flex gap-2.5 text-[0.9rem] leading-snug">
                        <span
                          className={`mt-0.5 inline-block h-2 w-2 shrink-0 rounded-full ${
                            (item.weight ?? 1) > 1 ? "bg-risk-high" : "bg-risk-medium"
                          }`}
                          aria-hidden
                        />
                        <span className="text-ink">
                          {item.label}
                          <span className="text-muted"> — {group}</span>
                        </span>
                      </li>
                    ))}
                  </ol>
                )}
                {computed.allGaps.some((g) => (g.item.weight ?? 1) > 1) && (
                  <p className="mt-3 text-[0.78rem] text-muted">
                    Red markers are core items most likely to hold up an assessment; amber are
                    supporting evidence.
                  </p>
                )}
              </div>

              <ActionPlanPrint items={gapItems} values={actions} />

              <PrintFooter toolName="SSIP readiness checker" />
            </div>

            <ActionPlanner items={gapItems} values={actions} onChange={setAction} onClearAll={clearActions} />

            <div className="flex justify-end">
              <PrintButton tool="ssip-readiness-checker" label="Print / save gap report as PDF" />
            </div>
          </>
        ) : (
          <div className="card border-dashed p-6 text-center text-sm text-muted">
            Work through the checklist to see your readiness for each area and a prioritised list of
            the gaps to close before you submit to an SSIP scheme.
          </div>
        )}
      </div>
    </div>
  );
}
